<template>
  <div class="px-2 py-4">
    <div class="mb-4 flex items-center justify-between">
      <div class="flex items-center">
        <MessageSquareText class="w-5 h-5 mx-2 text-gray-600" />
        <h1 class="text-gray-900">Internal Comments</h1>
        <span class="ml-2 text-sm text-gray-500">[{{ internalCommentsStore.totalCount }}]</span>
      </div>
      <div class="flex items-center gap-4">
        <!-- Search Bar -->
        <div class="relative w-[300px]">
          <Search
            class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search comments..."
            class="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            v-model="searchQuery"
            @input="handleSearch"
          />
        </div>
      </div>
    </div>

    <div
      v-if="internalCommentsStore.loading"
      class="flex justify-center items-center py-8 min-h-[calc(100vh-4rem)]"
    >
      <OuttalentLoader full-title />
    </div>

    <div
      v-else-if="internalCommentsStore.error"
      class="bg-red-50 border border-red-200 rounded-md p-4 mb-6"
    >
      <p class="text-red-600">{{ internalCommentsStore.error }}</p>
    </div>

    <div v-else>
      <!-- Reports Table -->
      <div class="bg-white">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200 w-32"
                >
                  Created
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  For User
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Comment
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="comment in internalCommentsStore.comments"
                :key="comment.id"
                class="hover:bg-gray-50 cursor-pointer"
                @click="!isBlurred && goToUserProfile(comment.user_details.username)"
              >
                <!-- Created By and When Column -->
                <td v-if="comment.created_by.username == 'admin'" class="py-3 px-4 border border-gray-100 w-36 align-top">
                  <div class="text-xs text-gray-500">Legacy Airtable Comment</div>
                </td>
                <td v-else class="py-3 px-4 border border-gray-100 w-36 align-top">
                  <div class="flex items-center gap-1">
                    <div v-if="comment.created_by.avatar_image" class="w-4 h-4 rounded-full overflow-hidden">
                      <img
                        :src="comment.created_by.avatar_image"
                        :alt="`${comment.created_by.first_name} ${comment.created_by.last_name}`"
                        class="w-full h-full object-cover"
                        :class="{ 'blur-md select-none': isBlurred }"
                      />
                    </div>
                    <div
                      v-else
                      class="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"
                    >
                      <div class="w-full h-full flex items-center justify-center" :class="{ 'blur-md select-none': isBlurred }">
                        <User class="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                    <span class="text-xs text-gray-900" :class="{ 'blur-md select-none': isBlurred }"
                      >{{ comment.created_by.first_name }} {{ comment.created_by.last_name }}</span
                    >
                  </div>
                  <div class="text-xs text-gray-500">{{ formatDateUtil(comment.created_at) }}</div>
                </td>
                <!-- User Column -->
                <td class="py-2 px-4 border border-gray-100 w-[20rem] align-top">
                  <div class="flex items-center gap-3">
                    <FellowAvatarDropdown
                      :user-id="comment.user_details.id"
                      :username="comment.user_details.username"
                      :avatar-src="comment.user_details?.avatar_image"
                      :name="`${comment.user_details.first_name} ${comment.user_details.last_name}`"
                      :curriculum-path="`/manage/campus/fellows/${comment.user_details.id}`"
                      :is-blurred="isBlurred"
                      size="md"
                      @click.stop
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900 flex items-center gap-2" :class="{ 'blur-md select-none': isBlurred }">
                        {{ comment.user_details.first_name }} {{ comment.user_details.last_name }}
                        <a
                          v-if="!isBlurred && comment.user_details.linkedin_profile_url"
                          @click.stop
                          :href="comment.user_details.linkedin_profile_url"
                          target="_blank"
                          class="text-xs text-gray-400 hover:scale-110 transition-all duration-300"
                        >
                          <img src="/linkedin_logo.svg" alt="LinkedIn" class="w-4 h-4" />
                        </a>
                        <a
                          v-if="!isBlurred && comment.user_details.telegram_username"
                          @click.stop
                          :href="comment.user_details.telegram_username"
                          target="_blank"
                          class="text-xs text-gray-400 hover:scale-110 transition-all duration-300"
                        >
                          <img src="/telegram_logo.svg" alt="Telegram" class="w-4 h-4" />
                        </a>
                      </div>
                      <div class="text-xs text-gray-400">
                        {{ formatBatchName(comment.fellow_details?.batch || '') }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Comment Column -->
                <td class="py-3 px-4 border border-gray-100 align-top">
                  <div class="flex text-xs gap-1">
                    <MarkdownRenderer :content="comment.comment" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="!internalCommentsStore.hasComments" class="text-center py-12">
          <div class="text-gray-500">No comments found</div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="internalCommentsStore.isPaginated" class="mt-6">
        <PaginationComponent
          :current-page="pagination.currentPage.value"
          :total-pages="internalCommentsStore.pagination.totalPages"
          :use-router="true"
          @update:page="pagination.updatePage"
          class="mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { formatDate as formatDateUtil } from '@/utils/formatDate'
import { MessageSquareText, Search } from 'lucide-vue-next'
import FellowAvatarDropdown from '@/components/common/FellowAvatarDropdown.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useRouterPagination } from '@/composables/useRouterPagination'
import { useDebounce } from '@/composables/useDebounce'
import { ref } from 'vue'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'
import { useInternalCommentsStore } from '@/stores/internalComments'
// import type { InternalComment } from '@/types/userProfile'
import { formatBatchName } from '@/utils/formatBatchName'
import MarkdownRenderer from '@/components/ui/markdown/MarkdownRenderer.vue'
import { useRouter } from 'vue-router'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const router = useRouter()
const { isBlurred } = usePrivacyMode()

const { debounce } = useDebounce(300) // Add debounce composable

const internalCommentsStore = useInternalCommentsStore()
// const selectedComment = ref<InternalComment | null>(null)

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  internalCommentsStore.fetchComments()
})

const pagination = useRouterPagination(async (page: number) => {
  await internalCommentsStore.fetchComments({ page })
})

const searchQuery = ref('')

// Use the composable's debounce function
const handleSearch = debounce(() => {
  internalCommentsStore.setSearch(searchQuery.value)
})

const goToUserProfile = (username: string) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/profile/${username}`)
}
</script>
